import type * as React from "react";
import { cn } from "@/lib/utils";
import { FileIcon } from "@/components/icons";

interface EmptyStateProps {
  /** 안내 제목 */
  title: string;
  /** 제목 아래 보조 설명 */
  description?: string;
  /** 64px 박스 안에 들어가는 36px 아이콘. 기본은 문서 아이콘 */
  icon?: React.ReactNode;
  /** 설명 아래 액션 (예: [고객사 등록하기]) */
  action?: React.ReactNode;
  className?: string;
}

/**
 * 모달·패널 본문의 빈 상태. 64px 아이콘 박스 + 제목 + 설명 + 액션을 세로로 쌓고
 * 남은 영역의 정중앙에 놓는다 (부모가 높이를 정해 줘야 중앙에 온다).
 *
 * 상세 패널의 작은 빈 상태(`PanelEmptyState`)와 규격이 다르다 — 이쪽은 검색 결과 영역처럼
 * 넓은 자리를 채우는 용도다.
 */
export function EmptyState({
  title,
  description,
  icon = <FileIcon size={36} color="labels-disable" />,
  action,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        // 부모가 높이를 주지 않아도 접히지 않게 최소 높이를 갖는다
        "flex min-h-70 w-full flex-1 flex-col items-center justify-center",
        className,
      )}
    >
      {/* 액션 버튼이 이 폭을 채운다 */}
      <div className="flex min-w-70 flex-col items-center gap-[19px]">
        <div className="flex size-16 shrink-0 items-center justify-center rounded-3xl bg-fills-neutral">
          {icon}
        </div>
        <div className="flex w-full flex-col items-center gap-1.5">
          <p className="typo-body-1-bold text-center text-labels-alternative">
            {title}
          </p>
          {description && (
            <p className="typo-body-2-medium text-center text-labels-assistive">
              {description}
            </p>
          )}
        </div>
        {action && <div className="flex w-full flex-col">{action}</div>}
      </div>
    </div>
  );
}

EmptyState.displayName = "EmptyState";
